/** @jsxImportSource theme-ui */
import React, { useCallback, useState } from 'react';
import { Box, Button, Text } from '@infotrack/zenith-ui';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@/hookform/resolvers/zod';
import {
  ImportTopFiveRequestSchema,
  type ImportTopFiveRequest,
  validateCsvFile,
  formatFileSize,
} from '../types/import.types';
import { type PeriodOption } from '../hooks/usePeriods';
import { PeriodSelector } from '@/shared/components';
import {
  formContainerStyles,
  sectionStyles,
  labelStyles,
  dropZoneStyles,
  fileInfoStyles,
  errorTextStyles,
  instructionsBoxStyles,
  instructionsTitleStyles,
  instructionsListStyles,
} from './importUploadForm.styles';

interface ImportUploadFormProps {
  onSubmit: (data: ImportTopFiveRequest & { startDate: string; endDate: string }) => void;
  isUploading: boolean;
}

/**
 * CSV file upload form with drag-drop support and period selection.
 * Uses React Hook Form + Zod for validation.
 */
export const ImportUploadForm: React.FC<ImportUploadFormProps> = ({ onSubmit, isUploading }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ImportTopFiveRequest>({
    resolver: zodResolver(ImportTopFiveRequestSchema),
    defaultValues: {
      periodId: '',
    },
  });

  const handleFormSubmit = (data: ImportTopFiveRequest) => {
    // Parse the selected period to get start/end dates
    const selectedPeriod = JSON.parse(data.periodId) as PeriodOption;
    onSubmit({
      ...data,
      startDate: selectedPeriod.startDate ?? '',
      endDate: selectedPeriod.endDate ?? '',
    });
  };

  const handleFileChange = useCallback(
    (file: File | null) => {
      setFileError(null);

      if (!file) {
        setSelectedFile(null);
        return;
      }

      const error = validateCsvFile(file);
      if (error) {
        setFileError(error);
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setValue('file', file, { shouldValidate: true });
    },
    [setValue]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragging(false);

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        handleFileChange(files[0] || null);
      }
    },
    [handleFileChange]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        handleFileChange(files[0] || null);
      }
    },
    [handleFileChange]
  );

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)}>
      <Box sx={formContainerStyles}>
        {/* File Upload Zone */}
        <Box sx={sectionStyles}>
          <Box sx={labelStyles}>
            <Text variant="subtitle">CSV File</Text>
          </Box>
          <Box
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            sx={dropZoneStyles(isDragging, !!fileError)}
            onClick={() => document.getElementById('file-input')?.click()}
          >
            <input
              id="file-input"
              type="file"
              accept=".csv"
              onChange={handleFileInputChange}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <Box sx={fileInfoStyles}>
                <Box sx={{ fontWeight: 'bold' }}>
                  <Text variant="medium">{selectedFile.name}</Text>
                </Box>
                <Box sx={{ color: 'neutral/600' }}>
                  <Text variant="caption">{formatFileSize(selectedFile.size)}</Text>
                </Box>
              </Box>
            ) : (
              <Box sx={fileInfoStyles}>
                <Text variant="medium">Drag and drop your CSV file here</Text>
                <Box sx={{ color: 'neutral/600' }}>
                  <Text variant="caption">or click to browse</Text>
                </Box>
              </Box>
            )}
          </Box>
          {fileError && (
            <Box sx={errorTextStyles}>
              <Text variant="caption">{fileError}</Text>
            </Box>
          )}
          {errors.file && (
            <Box sx={errorTextStyles}>
              <Text variant="caption">{errors.file.message as string}</Text>
            </Box>
          )}
        </Box>

        {/* Period Selection */}
        <Box sx={sectionStyles}>
          <PeriodSelector
            name="periodId"
            control={control}
            disabled={isUploading}
            label="Top5 Period"
            placeholder="Select a period..."
            error={errors.periodId?.message as string}
          />
        </Box>

        {/* Submit Button */}
        <Box>
          <Button type="submit" disabled={isUploading || !selectedFile} fullWidth>
            {isUploading ? 'Uploading...' : 'Import'}
          </Button>
        </Box>

        {/* Instructions */}
        <Box sx={instructionsBoxStyles}>
          <Box sx={instructionsTitleStyles}>
            <Text variant="medium">CSV Format Requirements</Text>
          </Box>
          <Box sx={instructionsListStyles}>
            <Text variant="small">• <strong>FirstName:</strong> Staff member first name</Text>
            <Text variant="small">• <strong>Surname:</strong> Staff member surname</Text>
            <Text variant="small">• <strong>Name:</strong> Top5 goal name</Text>
            <Text variant="small">• <strong>Weight:</strong> Priority weight (0-100)</Text>
            <Text variant="small">• <strong>EmployeeId:</strong> Staff employee ID</Text>
          </Box>
        </Box>
      </Box>
    </form>
  );
};
